<script lang="ts">
  /* ONE PANEL CHROME, BOTH VIEWPORTS.
   *
   * Head, scrolling body, pinned foot. In the docked column it is a column; in
   * the bottom sheet it is the same markup with the sheet's grabber above it.
   *
   * ═══ THIS IS WHAT RETIRES THE `variant` PROPS ═══
   *
   * `Layers.svelte` carries `inSheet`, `ChartTools.svelte` carries `variant`,
   * and `RouteContent.svelte` was extracted into a file of its own purely so
   * the desktop box and the phone sheet could not drift apart. All three are
   * the same missing abstraction: a panel had to know which container it was
   * in. It does not, now — the container decides its own chrome and the panel
   * is only ever its body.
   *
   * ═══ THE HEAD SAYS A DIFFERENT THING IN EACH ═══
   *
   * And that is deliberate rather than an inconsistency. In the column the
   * panel is already open and visible, so the head is a TITLE — you know what
   * you are looking at, you need to know what it is called. In the sheet the
   * head is visible at PEEK, when the body is not, so it is the ANSWER: "hold
   * 1h04 at Dodd · arrive 11:56". A sheet whose 96 px of peek says "TRIP" has
   * spent them telling you what you just tapped.
   */
  import type { Snippet } from 'svelte';
  import { getContext } from 'svelte';
  import Button from '../primitives/Button.svelte';
  import Icon from '../primitives/Icon.svelte';
  import { SHEET_MEASURE, type ReportHeight } from './measure';

  let {
    title,
    /** The answer, for the sheet's head. Falls back to the title — which is
     *  worse, and is why every registered panel should supply one. */
    peek,
    /** Beside the title: a count, a chip, a clear-all. */
    aside,
    /** Pinned below the scroll. See the note on `.foot`. */
    foot,
    onclose,
    /** `column` is the docked panel and the tablet drawer; `sheet` is the phone
     *  surface, where the grabber and the height belong to `BottomSheet`. */
    variant = 'column',
    children
  }: {
    title: string;
    peek?: Snippet;
    aside?: Snippet;
    foot?: Snippet;
    onclose?: () => void;
    variant?: 'column' | 'sheet';
    children: Snippet;
  } = $props();

  /* ═══ REPORTING HEIGHT, ONLY WHEN SOMEBODY IS LISTENING ═══
   *
   * Provided by `<BottomSheet>` and by nothing else, so in the docked column
   * this whole block costs one `getContext` and stops. See measure.ts for why
   * it is a contract rather than the sheet reaching in with a selector.
   *
   * The measuring stick is `.inner`, which HUGS its children — never the scroll
   * box. `scrollHeight` on the body returns `max(content, clientHeight)`, so
   * once the sheet was tall the measurement returned the sheet's own height and
   * the cap became a feedback loop that could only ever grow: 166 px of empty
   * panel that the arithmetic swore was zero. A wrapper that hugs cannot lie
   * this way.
   *
   * Re-measured rather than computed once because the content changes under the
   * user's hand — ticking USE CURRENTS adds a whole schedule — so a constant
   * would be wrong at the moment it mattered.
   */
  const report = getContext<ReportHeight | undefined>(SHEET_MEASURE);

  let headEl = $state<HTMLElement | null>(null);
  let innerEl = $state<HTMLElement | null>(null);
  let footEl = $state<HTMLElement | null>(null);

  $effect(() => {
    if (!report || !headEl || !innerEl) return;
    const measure = () => {
      /* The body's own vertical padding, which `.inner` does not carry. */
      const pad = 16;
      report(
        headEl!.offsetHeight + innerEl!.getBoundingClientRect().height + (footEl?.offsetHeight ?? 0) + pad
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(headEl);
    ro.observe(innerEl);
    if (footEl) ro.observe(footEl);
    return () => ro.disconnect();
  });
</script>

<div class="panel {variant}">
  <div class="head" bind:this={headEl}>
    {#if variant === 'sheet' && peek}
      <span class="peekline">{@render peek()}</span>
    {:else}
      <span class="ttl">{title}</span>
    {/if}
    {#if aside}<span class="aside">{@render aside()}</span>{/if}
    {#if onclose}
      <Button intent="ghost" icon ariaLabel="Close {title}" onclick={onclose}>
        <Icon name="close" size={16} />
      </Button>
    {/if}
  </div>

  <div class="body">
    <!-- The measuring stick: a block exactly as tall as what is in it, whatever
         the scroll box around it is doing. `display: flow-root` so a child's
         margin cannot collapse through and make the measured height smaller
         than what is drawn. -->
    <div class="inner" bind:this={innerEl}>{@render children()}</div>
  </div>

  {#if foot}
    <div class="foot" bind:this={footEl}>{@render foot()}</div>
  {/if}
</div>

<style>
  .panel {
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
  }
  .head {
    display: flex;
    /* Centred, not flex-start. The close button is a 44 px target, and aligning
       it to the top of a one-line peek dropped it below the text it belongs
       to — which a specimen shot caught and no assertion would have. */
    align-items: center;
    gap: var(--sp-4);
    padding: var(--sp-5) var(--sp-5) var(--sp-4);
    flex: none;
  }
  /* In the sheet the grabber above already provides the top margin, and a
     second one makes the peek line look like it is falling out of the panel. */
  .panel.sheet .head {
    padding-top: 0;
  }
  .ttl,
  .peekline {
    flex: 1;
    min-width: 0;
    font: var(--fs-lead) / var(--lh-ui) var(--mono);
    color: var(--ink);
    letter-spacing: var(--tr-ui);
  }
  .aside {
    flex: none;
  }

  .body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    /* A fling inside a panel must not rubber-band the chart behind it. */
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 0 var(--sp-5) var(--sp-5);
  }
  .inner {
    display: flow-root;
  }

  /* ═══ PINNED, AND THAT IS THE POINT ═══
   *
   * The one action a panel exists to offer must not scroll off the bottom of a
   * schedule nobody thought to scroll. Two versions of this fault are in the
   * shipped app and the mockups: the desktop route box caps itself at
   * `calc(100dvh - 190px)` and puts EXPORT GPX below the fold, and the first
   * landscape mockup did the same to "Use 06:40 Sunday" on a 393 px screen.
   *
   * The safe-area inset is here rather than on the sheet because this is the
   * lowest thing in either container. */
  .foot {
    flex: none;
    padding: var(--sp-4) var(--sp-5) calc(var(--sp-5) + env(safe-area-inset-bottom));
    border-top: 1px solid var(--line);
    background: var(--s-1);
  }
</style>
